import { FiTruck } from 'react-icons/fi'

import {
    CRM_CONTRACT_FILE_EXTENSIONS as CRM_FILE_EXTENSIONS,
    CRM_FILE_MAX_SIZE_MB,
} from '@/Containers/CrmSection/Components/Contracts'
import { exportToExcelButton } from '@/Containers/CrmSection/Components/Export'
import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { copyableListView, defineView, renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { formatPhone, formatPhones } from '@/Ship/Helpers/formatPhone'

import { CarrierVerificationButtons } from '../Components/CarrierVerification'
import { CARRIER_COST_FIELDS, CARRIER_FIELD_LABELS as L } from '../Contracts/CarrierFieldLabels'
import { CARRIER_FILE_FIELDS, CARRIER_FILE_FIELD_NAMES, carrierFileUploadUrl } from '../Contracts/CarrierFileCategory'
import { CARRIER_CONTACT_RESOURCE, CARRIER_REST_URL, CARRIER_STATUS_RESOURCE } from '../Contracts/ICarrier'
import { CarrierFormTemplate } from '../Templates/CarrierFormTemplate'

import type { CarrierStatusRef } from '../Contracts/ICarrier'
import type { CarrierFileField } from '../Contracts/CarrierFileCategory'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { ReactNode } from 'react'

interface CarrierRow {
    status?: CarrierStatusRef | null
    phones?: string[] | null
    _original?: CarrierRow
}

const sourceOf = (row: unknown): CarrierRow => {
    const item = (row ?? {}) as CarrierRow

    return item._original ?? item
}

/**
 * Колонки, які ядро само не намалює як треба (`SPEC-010` §9.2):
 *
 *  - **«Статус»** — бейдж кольору довідника (`StatusTag`). Ядровий бейдж
 *    шукає статус серед `options` ресурсу за рядком, а в перевізника статус —
 *    вкладений рядок довідника;
 *  - **«Телефони»** — у базі самими цифрами, у колонці — «+38 (067) 123-45-67».
 */
const AddCarrierColumnRenderers = ({ columns }: { columns: Array<Record<string, unknown>> }) => {
    columns.forEach((column) => {
        const key = String(column['key'] ?? '')

        if (key === 'status') {
            column['render'] = (_: unknown, row: unknown): ReactNode => {
                const status = sourceOf(row).status

                return status && typeof status.title === 'string' ? (
                    <StatusTag label={status.title} color={status.color} />
                ) : (
                    '—'
                )
            }
        }

        if (key === 'phones') {
            column['render'] = (_: unknown, row: unknown): ReactNode => formatPhones(sourceOf(row).phones) || '—'
        }
    })

    return columns
}

/** Рядок із межею довжини колонки беку (`SPEC-010` §5.1). */
const text = (label: string, maxLength: number): IFieldConfig => ({ label, inputProps: { maxLength } })

/** Довідник-select (`SPEC-007`): один рядок або кілька. */
const dictionary = (label: string, resource: string, isMultiple = false): IFieldConfig => ({
    label,
    type: 'select',
    resource,
    ...(isMultiple ? { isMultiple: true } : {}),
})

/**
 * Перемикач перевірок верифікації. У перегляді — «Так» / «Ні»: ядро малює
 * `true` порожнім місцем, а `false` — прочерком, і «не перевірено» не
 * відрізнити від «нічого не вказано».
 */
const checkField = (label: string): IFieldConfig => ({
    label,
    type: 'switch',
    defaultValue: false,
    view: defineView((value) => (value === true ? 'Так' : 'Ні')),
})

/** Сума: 0 за замовчуванням (`SPEC-008` §4.8), у картці — разом із валютою. */
const amountField = (label: string): IFieldConfig => ({
    label,
    type: 'number',
    defaultValue: 0,
    inputProps: { min: 0, precision: 2, style: { width: '100%' } },
})

/** Валюта суми: вузький select із кодом валюти (`SPEC-008` §4.8). */
const currencyField = (): IFieldConfig => ({
    label: 'Валюта',
    type: 'select',
    resource: 'Setting.ListCurrency',
    responseLabel: (item: { code?: unknown; title?: unknown }) =>
        typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
})

/** Лише перегляд: ставить сервер (переходи верифікації, аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Файлове поле категорії (`SPEC-010` §9.4, R-09): кілька файлів, крок 1 — на
 * `/services/carrier_files?category=…`, білий список і межа розміру — ті самі,
 * що в договору (`CrmFileMimePolicy`, nginx ядра).
 */
const fileField = (field: CarrierFileField): IFieldConfig => {
    const category = CARRIER_FILE_FIELDS.find((item) => item.field === field)?.category ?? 'other'

    return {
        label: L[field],
        type: 'file',
        isMultiple: true,
        uploadUrl: carrierFileUploadUrl(category),
        uploadAccept: CRM_FILE_EXTENSIONS,
        uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
        showViewIcon: false,
        showEditIcon: false,
    }
}

/** Мінімум поля й форми, з яким працює `depFn` ядра. */
interface ContactPersonDepFnArgs {
    field: {
        inputProps?: Record<string, unknown>
        mergeInputProps?: (props: Record<string, unknown>) => void
    }
    formContext: {
        contextKey?: string
        values?: { uuid?: unknown }
    }
}

/**
 * «Контактна особа» — лише контакт **цього** перевізника (R-08): селект
 * питає контакти з префільтром `carrier.uuid`, як вкладка «Контакти». У
 * легасі фільтр був лише в картці, а у формі створення селект показував
 * контакти всіх перевізників — тут поля у формі створення немає взагалі
 * (контактів ще немає), а чужий контакт бек однаково відхиляє 422.
 *
 * Повертає `undefined`, а не `false`: `false` з `depFn` ядро читає як
 * «сховати поле».
 */
const filterContactsOfThisCarrier = ({ field, formContext }: ContactPersonDepFnArgs): void => {
    if (formContext.contextKey === 'filterFormContext') return

    const uuid = formContext.values?.uuid

    if (typeof uuid !== 'string' || uuid === '') return

    const current = field.inputProps?.['filter'] as Record<string, unknown> | undefined

    // Фільтр — об'єкт: ядро порівнює такі пропси за посиланням і без цієї
    // перевірки перезаписувало б їх на кожну зміну форми
    if (current?.['carrier.uuid'] === uuid) return

    field.mergeInputProps?.({ filter: { 'carrier.uuid': uuid } })
}

/**
 * Поля, яких бек від форми не приймає (`carrier:create` / `carrier:update`,
 * `SPEC-010` §5.7): статус і верифікацію змінюють лише переходи, аудит —
 * ядро. Не шлемо їх, щоб тіло запиту не обіцяло зміни, якої не буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'status',
    'verifiedBy',
    'verifiedAt',
    'commentVerification',
    'isVerified',
    'isBlacklisted',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

/**
 * Тіло запиту картки перед відправкою: без полів лише для читання; порожня
 * сума — 0 (бек тримає NOT NULL і інакше відповів би 422 на перевізника, у
 * якого суму просто не вводили).
 */
const prepareCarrierPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    // Контактна особа — лише в збереженого перевізника: у новому контактів
    // ще немає, і поля у формі створення немає (R-08)
    if (typeof formContext?.values?.['@id'] !== 'string') delete values['contactPerson']

    for (const { amount } of CARRIER_COST_FIELDS) {
        const value = values[amount]

        if (value === null || value === undefined || value === '') values[amount] = 0
    }
}

/**
 * Перевізники (`SPEC-010` §5.1, §8.1–§8.2, §9.2): реєстр на 13 колонок і
 * 12 фільтрів у порядку легасі (`CarrierPage.jsx:29-57`), 20 рядків на
 * сторінку, сортування за «Датою зміни» — на беку (R-17).
 *
 * Права — лише з беку (`SPEC-003` §1.3, `SPEC-010` §14): «Додати» —
 * колекційне `create` (`ResourceCorrespondenceConfig`), «Редагувати» /
 * «Видалити» — `_permissions` запису: правило «лише логіст» (R-06) знає
 * `CarrierRule`. Переходи верифікації — пункти випадного списку картки
 * (`CarrierVerificationButtons`, §9.5), теж лише за `_permissions`. Ролей
 * ресурс не питає.
 *
 * Над таблицею — рядок статусів довідника «Статуси перевізників» з
 * лічильниками `GET /services/carriers/counters` (`StatusFilterBar`). У
 * шапці — «Експорт в Excel» за колекційним правом `export` (§10).
 *
 * Файли за категоріями — двокроково (`SPEC-008` §5.7.1): чотири поля
 * вантажать файли сиротами, «Зберегти» прикріплює нові через один
 * `fileUuids` (`attachUploadedFiles` з переліком полів, Р10-4).
 */
export const CarrierResource: IResourceConfig = {
    title: 'Перевізники',
    fields: {
        id: {
            label: L.id,
            type: 'number',
            isEditable: false,
        },
        title: {
            label: L.title,
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        fullName: text(L.fullName, 150),
        // Статус ставлять створення («Новий», R-01) і переходи — у формі лише
        // перегляд. `inputProps.disabled` вимикає й ядрову колонку, яка інакше
        // слала б `PATCH {status}` (вартовий колонки статусу)
        status: {
            label: L.status,
            type: 'status',
            statusResource: CARRIER_STATUS_RESOURCE,
            isEditable: false,
            inputProps: { disabled: true },
        },
        responsibles: {
            label: L.responsibles,
            type: 'select',
            isMultiple: true,
            // Обов'язковий і на беку (R-07); у формі створення — поточний користувач
            isRequired: true,
            // Список співробітників ядро віддає лише з активними (`SPEC-005`)
            resource: 'Setting.User',
            view: renderUsersView,
        },
        emails: {
            label: L.emails,
            isMultiple: true,
            // Пошти в базі в нижньому регістрі (`ContactDataNormalizer`)
            filterValueTransform: (value: string) => value.trim().toLowerCase(),
            view: copyableListView(),
        },
        phones: {
            label: L.phones,
            type: 'phone',
            isMultiple: true,
            // У базі самими цифрами — у картці той самий формат, що в реєстрі,
            // і копіювання біля кожного
            view: copyableListView(formatPhone),
        },
        lardi: text(L.lardi, 255),
        comment: {
            label: L.comment,
            type: 'textarea',
            inputProps: { maxLength: 4096 },
        },
        tags: {
            label: L.tags,
            isMultiple: true,
        },
        carTypes: dictionary(L.carTypes, 'Setting.ListCarType', true),
        carParkSize: dictionary(L.carParkSize, 'Setting.ListCarParkSize'),
        countries: dictionary(L.countries, 'Setting.ListCountry', true),
        formTaxation: dictionary(L.formTaxation, 'Setting.ListFormTaxation'),
        address: text(L.address, 255),
        iban: text(L.iban, 30),
        bank: text(L.bank, 150),
        mfo: text(L.mfo, 150),
        edrpou: {
            label: L.edrpou,
            // Точний збіг, як у легасі (`exact edrpou`); не унікальний (R-04)
            filterOperator: '=',
            // Довжину (8–12) перевіряє бек — текст 422 ляже під поле
            inputProps: { maxLength: 12 },
        },
        numberVAT: text(L.numberVAT, 150),
        ipn: {
            label: L.ipn,
            filterOperator: '=',
            inputProps: { maxLength: 12 },
        },
        manager: text(L.manager, 100),
        forwarder: dictionary(L.forwarder, 'Setting.ListLegalEntity'),
        contactPerson: {
            label: L.contactPerson,
            type: 'select',
            resource: CARRIER_CONTACT_RESOURCE,
            depFn: filterContactsOfThisCarrier,
        },
        carOwnershipType: dictionary(L.carOwnershipType, 'Setting.ListCarOwnershipType'),
        isCoincidenceCarRegistration: checkField(L.isCoincidenceCarRegistration),
        isInspectionEdrpou: checkField(L.isInspectionEdrpou),
        isCarInspectionOpendatabot: checkField(L.isCarInspectionOpendatabot),
        lardiChanges: text(L.lardiChanges, 255),
        // Рядок, а не число — як колонка легасі (varchar 150, НП-8)
        numberPositiveReviews: text(L.numberPositiveReviews, 150),
        numberNegativeReviews: text(L.numberNegativeReviews, 150),
        mentionInGoogle: text(L.mentionInGoogle, 255),
        approximateCostCargo: amountField(L.approximateCostCargo),
        approximateCostCargoCurrency: currencyField(),
        cmrInsurance: text(L.cmrInsurance, 255),
        recommendation1: text(L.recommendation1, 255),
        recommendation2: text(L.recommendation2, 255),
        recommendation3: text(L.recommendation3, 255),
        filesRegistrationDocuments: fileField('filesRegistrationDocuments'),
        filesTechnicalPassports: fileField('filesTechnicalPassports'),
        filesCmrInsurance: fileField('filesCmrInsurance'),
        filesOther: fileField('filesOther'),
        verifiedBy: readOnly({
            label: L.verifiedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        verifiedAt: readOnly({
            label: L.verifiedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        commentVerification: readOnly({
            label: L.commentVerification,
            type: 'textarea',
        }),
        // Службова інформація: бек віддає її для кожного запису; у формі — лише показ
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    // 13 колонок у порядку легасі (`CarrierPage.jsx:45-57`)
    table: {
        showColumns: [
            'id',
            'title',
            'fullName',
            'status',
            'responsibles',
            'tags',
            'emails',
            'phones',
            'formTaxation',
            'iban',
            'mfo',
            'edrpou',
            'ipn',
        ],
        // Статус і телефони малюємо самі — автотрансформація тут лише заважає
        dataSourceNoTransform: ['status', 'phones'],
        eventHandlers: {
            onBuildColumns: { AddCarrierColumnRenderers },
        },
    },
    // 12 фільтрів у порядку легасі (`CarrierPage.jsx:29-44`); селекти — одним
    // значенням, як у легасі (`SPEC-010` §8.2)
    filter: {
        includeFields: [
            'responsibles',
            'status',
            'title',
            'phones',
            'emails',
            'edrpou',
            'ipn',
            'carTypes',
            'countries',
            'id',
            'tags',
            'formTaxation',
        ],
        // «Статус» — рядок довідника: варіанти бере з того самого довідника,
        // що й рядок статусів над таблицею, і пише код (`StatusFilterBar`)
        fieldProps: {
            status: { input: StatusDictionarySelect },
        },
    },
    list: {
        pagination: { pageSize: 20 },
    },
    // «Експорт в Excel» (§10): поточна вибірка таблиці файлом «Перевізники.xlsx»;
    // кнопку видно за колекційним правом `export` (A)
    headerPage: {
        eventHandlers: {
            onButtons: {
                ExportToExcel: exportToExcelButton('Перевізники.xlsx'),
            },
        },
    },
    form: {
        eventHandlers: {
            // Крок 2: нові файли всіх чотирьох категорій — одним `fileUuids`,
            // самі поля — геть із тіла (Р10-4)
            onBeforeTransformSendSaveRequest: {
                carrierFileUuids: attachUploadedFiles(CARRIER_FILE_FIELD_NAMES),
            },
            onBeforeSendSaveRequest: {
                CarrierPayload: prepareCarrierPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            // Кнопки верифікації — лише в перегляді й лише за `_permissions`
            // запису (`SPEC-010` §9.5): перехід не має йти з незбереженими правками
            view: { template: CarrierFormTemplate, onButtons: CarrierVerificationButtons },
            edit: { template: CarrierFormTemplate },
            create: { template: CarrierFormTemplate },
        },
    },
    // Шухляда 1200, як у легасі (`CarrierResource.jsx:323`): два стовпці полів
    drawer: {
        width: 1200,
    },
    restUrl: CARRIER_REST_URL,
    pageUrl: '/carriers',
    pageConfig: {
        title: 'Перевізники',
        icon: <FiTruck />,
    },
    // Пошук у селектах «Перевізник» справ і листів (`SPEC-010` §8.1, `_search`)
    settings: {
        searchFields: ['title', 'fullName'],
    },
}
